import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatMoney } from '@/lib/money';

import {
  MODULE_PRICES_QUERY_KEY,
  addTenantSubscriptionItem,
  getModulePrices,
  getTenantSubscription,
  tenantSubscriptionQueryKey,
} from '../api/subscription.graphql';
import {
  MODULE_REASON_LIMITS,
  getModuleImpact,
  setTenantModule,
  type TenantModule,
} from '../api/tenant-modules.graphql';
import { moduleNames, plural } from '../module-labels';

interface Props {
  tenantId: string;
  module: TenantModule;
  /** Todos los módulos del tenant (para nombrar dependencias). */
  modules: TenantModule[];
  /** `note`: resultado de la suscripción (OS-1723), si se pidió agregarla. */
  onDone: (modules: TenantModule[], note?: string) => void;
  /** El API rechazó el cambio: el estado pudo cambiar en otra sesión, hay que recargarlo. */
  onRejected: () => void;
  onCancel: () => void;
  /** OS-1723 — `platform.billing.read`: ver el impacto en la suscripción. */
  canReadBilling?: boolean;
  /** OS-1723 — `platform.billing.write`: proponer agregar el módulo a la suscripción. */
  canWriteBilling?: boolean;
}

/**
 * OS-1716 — confirmación de habilitar/deshabilitar un módulo: motivo obligatorio (auditoría),
 * impacto y dependencias. Si el API lo rechaza (dependencias, otro admin lo cambió), el mensaje del
 * API se muestra tal cual en el diálogo.
 */
export function ModuleToggleDialog({
  tenantId,
  module,
  modules,
  onDone,
  onRejected,
  onCancel,
  canReadBilling = false,
  canWriteBilling = false,
}: Props): JSX.Element {
  const enabling = !module.enabled;
  const names = new Map(modules.map((m) => [m.key, m.name]));
  // Solo presentación: qué módulos del catálogo dependen de este (lo calcula el API en `dependsOn`).
  const dependents = modules.filter((m) => m.dependsOn.includes(module.key)).map((m) => m.key);

  const impact = useQuery({
    queryKey: ['platform-tenant-module-impact', tenantId, module.key],
    queryFn: () => getModuleImpact(tenantId, module.key),
    enabled: !enabling,
    staleTime: 0,
  });
  // OS-1723 — suscripción del tenant y precio de lista del módulo (solo con billing.read).
  const subscription = useQuery({
    queryKey: tenantSubscriptionQueryKey(tenantId),
    queryFn: () => getTenantSubscription(tenantId),
    enabled: canReadBilling,
    staleTime: 0,
  });
  const prices = useQuery({
    queryKey: MODULE_PRICES_QUERY_KEY,
    queryFn: getModulePrices,
    enabled: canReadBilling && enabling,
  });
  const [addToSubscription, setAddToSubscription] = useState(false);
  const listPrice =
    prices.data?.modules.find((price) => price.moduleKey === module.key)?.amountMonthly ?? null;
  const billedLine = subscription.data?.items.find((item) => item.moduleKey === module.key);
  // D1A — se propone solo en tenants activos o en mora, con precio de lista y sin línea vigente.
  const canPropose =
    enabling &&
    canWriteBilling &&
    subscription.data?.mrrKind === 'real' &&
    listPrice !== null &&
    !billedLine;

  const mutation = useMutation({
    mutationFn: async (reason: string) => {
      const modules = await setTenantModule({
        tenantId,
        moduleKey: module.key,
        enabled: enabling,
        reason,
      });
      if (!(canPropose && addToSubscription)) {
        return { modules, note: undefined };
      }
      // El módulo ya quedó habilitado: si agregar la línea falla, se informa sin deshacerlo.
      try {
        await addTenantSubscriptionItem(tenantId, module.key);
        return { modules, note: `Se agregó a la suscripción por ${formatMoney(listPrice)} al mes.` };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'error desconocido';
        return { modules, note: `No se pudo agregar a la suscripción: ${message}` };
      }
    },
    onSuccess: ({ modules, note }) => onDone(modules, note),
    onError: onRejected,
  });

  return (
    <ConfirmDialog
      title={`${enabling ? 'Habilitar' : 'Deshabilitar'} ${module.name}`}
      confirmLabel={enabling ? 'Habilitar' : 'Deshabilitar'}
      cancelLabel="Volver"
      danger={!enabling}
      pending={mutation.isPending}
      error={mutation.error?.message ?? null}
      reason={{
        label: 'Motivo (queda en la auditoría)',
        placeholder: enabling
          ? 'Ej.: el cliente contrató el módulo de nómina'
          : 'Ej.: el cliente canceló el módulo en la renovación',
        ...MODULE_REASON_LIMITS,
      }}
      onConfirm={(reason) => mutation.mutate(reason)}
      onCancel={onCancel}
    >
      {enabling ? (
        <>
          <p>
            Con el control de módulos activo, los usuarios del tenant con permisos de {module.name}
            recuperan el acceso a sus pantallas, endpoints y apps.
          </p>
          {canPropose ? (
            <label>
              <input
                type="checkbox"
                checked={addToSubscription}
                onChange={(event) => setAddToSubscription(event.target.checked)}
              />{' '}
              Agregar {module.name} a la suscripción por {formatMoney(listPrice)} al mes (precio de
              lista). Sin marcar, el módulo queda habilitado sin cobro.
            </label>
          ) : null}
        </>
      ) : (
        <>
          <p>
            Con el control de módulos activo, los usuarios del tenant pierden el acceso a{' '}
            {module.name}: pantallas, endpoints y apps del módulo. Sus datos se conservan y vuelven a
            estar disponibles al habilitarlo de nuevo.
          </p>
          <p aria-live="polite">
            {impact.isLoading
              ? 'Calculando el impacto…'
              : impact.data
                ? `Afecta a ${plural(impact.data.activeUsers, 'usuario activo', 'usuarios activos')} con permisos de este módulo` +
                  (impact.data.activeOperationalUsers > 0
                    ? ` y a hasta ${plural(impact.data.activeOperationalUsers, 'usuario operativo', 'usuarios operativos')} (mesero, caja, cocina o planta).`
                    : '.')
                : 'No se pudo calcular el impacto.'}
          </p>
          {billedLine ? (
            <p>
              Se cobra en la suscripción: al deshabilitarlo se cierra su línea y el MRR del tenant
              baja {formatMoney(billedLine.monthlyAmount, billedLine.currency)} al mes.
            </p>
          ) : canReadBilling ? null : (
            <p>Si el módulo se cobra, su línea de suscripción se cierra al deshabilitarlo.</p>
          )}
        </>
      )}
      {module.dependsOn.length > 0 ? (
        <p>Depende de: {moduleNames(module.dependsOn, names)}.</p>
      ) : null}
      {!enabling && dependents.length > 0 ? (
        <p>
          Mientras esté deshabilitado no se podrá habilitar: {moduleNames(dependents, names)}.
        </p>
      ) : null}
      <p>
        El cambio se guarda y queda auditado. El acceso solo se bloquea si el control de módulos está
        activo en el ambiente; en ese caso llega al tenant en menos de 5 segundos.
      </p>
    </ConfirmDialog>
  );
}
